/**
 * The invoice table (R5), over R3 rows.
 *
 * NOTHING HERE IS DERIVED. The order of the rows, how many there are and how
 * many exist in total are all the server's answers; this component reports a
 * header click and an offset change upward and paints whatever comes back. There
 * is no local sort and no local slice, so the footer's "68" and the rows above
 * it can never describe different populations.
 *
 * THREE PLACES A PLAUSIBLE VALUE COULD BE INVENTED, AND ARE NOT:
 *
 *   THE PERIOD. An invoice raised on 2 October can bill September, so the issue
 *   date is not a fallback for the billing month — it is a different fact. R3
 *   says explicitly whether the period is `authoritative`, `unavailable` or
 *   `ambiguous`, and there is deliberately no fourth value for "guessed". Where
 *   the server declined to name a month, this table declines too, and says why.
 *
 *   THE PAID FIGURE. `amount` and `open_amount` are two provider columns and
 *   their difference is not a third: subtracting them would invent a payment
 *   nobody reported. A partially settled invoice therefore states both numbers
 *   and stops.
 *
 *   THE CURRENCY. `bexio_invoices.currency` has been nullable since migration
 *   159, and NULL is a third state — not CHF, not foreign, but "nobody told us".
 *   `fmtInvoiceMoney` carries that distinction, which is why the amount goes
 *   through it rather than through a franc formatter.
 *
 * NO SOURCE COLUMN. The whole view is one source; a column repeating it on every
 * row would be the first place a second one could appear.
 *
 * NO WRITE ACTION. R5 adds no cancel, no mark-paid and no edit. The only action
 * offered is a link to a detail route this repository already has — which the
 * native side has and the Bexio side does not, so the Bexio side offers none
 * rather than a link that lands somewhere else.
 */
import React from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  ExternalLink,
  Inbox,
} from "lucide-react";
import { Link } from "react-router-dom";

import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
import { fmtInvoiceMoney } from "../../../components/admin/bexio/bexioFormat";
import { formatDateOnly } from "../../../utils/datetime";

/** The eight columns, and which of them R3 will order by. */
const COLUMNS = [
  { key: "invoice_number", label: "Rechnung", sortField: "invoice_number" },
  { key: "party", label: "Mieter / Objekt" },
  // Hidden on narrow viewports: it is the column a reader can most afford to
  // lose, and dropping it keeps amount, status and due date on screen without
  // horizontal scrolling. The header stays in the markup, so the column is a
  // CSS decision rather than a different table.
  { key: "period", label: "Zeitraum", narrow: "hidden lg:table-cell" },
  { key: "amount", label: "Betrag", sortField: "amount", align: "text-right" },
  { key: "status", label: "Status" },
  { key: "issue_date", label: "Rechnungsdatum", sortField: "issue_date" },
  { key: "due_date", label: "Fälligkeit", sortField: "due_date" },
  { key: "actions", label: "Aktionen", align: "text-right" },
];

/** The four words the product speaks, with a token pair for each. */
const STATUS = {
  paid: {
    label: "Bezahlt",
    cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
  unpaid: {
    label: "Offen",
    cls: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  },
  overdue: {
    label: "Überfällig",
    cls: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300",
  },
  cancelled: {
    label: "Storniert",
    cls: "border-border bg-muted text-muted-foreground",
  },
};

const PERIOD_HINT = {
  unavailable: "Kein eindeutig belegter Rechnungszeitraum.",
  ambiguous: "Mehrere Ledger-Claims — der Rechnungszeitraum ist mehrdeutig.",
};

const TH =
  "whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-left text-[10px] font-medium uppercase tracking-[0.6px] text-muted-foreground";
const TD = "px-2.5 py-2.5 align-top text-[12px] text-foreground";

/** A civil month, named. Built from an ALREADY DECIDED year/month, never a clock. */
function monthYearLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString("de-CH", {
    month: "long",
    year: "numeric",
  });
}

function StatusPill({ status }) {
  const meta = STATUS[status] || {
    label: status || "—",
    cls: "border-border bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * The billing month, or an explicit refusal to name one.
 *
 * Focusable so the explanation is reachable without a pointer: a marker only a
 * mouse can read is not an explanation.
 */
function PeriodCell({ period, periodStatus }) {
  if (periodStatus === "authoritative" && period) {
    return (
      <span className="whitespace-nowrap">
        {monthYearLabel(period.year, period.month)}
      </span>
    );
  }
  const hint = PERIOD_HINT[periodStatus] || PERIOD_HINT.unavailable;
  return (
    <span
      data-testid="invoice-period-quality"
      tabIndex={0}
      title={hint}
      className="inline-flex items-center gap-1 rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      —
      {periodStatus === "ambiguous" ? (
        <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
      ) : null}
      <span className="sr-only">{hint}</span>
    </span>
  );
}

/** Tenant on one line, object on the next. No id is ever shown as a label. */
function PartyCell({ row }) {
  const objectLabel = [row.unit?.label, row.room?.name].filter(Boolean).join(" · ");
  return (
    // R9: A CEILING, NOT A CUT. Swiss company tenants have names like
    // "Bernasconi-Hohenzollern von Wattenwyl Immobilien Treuhand AG", and one
    // of them used to widen this column until it dragged the whole table past
    // the viewport. The cell is now bounded and clipped —
    // but the DATA is untouched: the full name stays in the DOM, in the
    // accessible name, and in `title` for the mouse. Nothing here shortens what
    // the row SAYS, because a silently shortened tenant name is a wrong name.
    <div
      data-testid="invoice-party-cell"
      className="min-w-0 max-w-[220px]"
    >
      {row.tenant?.name ? (
        row.tenant.id ? (
          <Link
            to={`/admin/tenants/${row.tenant.id}`}
            title={row.tenant.name}
            className="block truncate text-foreground underline-offset-2 hover:underline"
          >
            {row.tenant.name}
          </Link>
        ) : (
          <span title={row.tenant.name} className="block truncate">
            {row.tenant.name}
          </span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
      {objectLabel ? (
        <div title={objectLabel} className="truncate text-[11px] text-muted-foreground">
          {objectLabel}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The amount, plus the outstanding part where the provider reported one.
 *
 * The second line appears only when it says something the first does not: a
 * fully outstanding invoice repeats its own total, and a native row has no
 * partial-payment model at all.
 */
function MoneyCell({ row }) {
  const showOpen =
    row.open_amount != null &&
    Number(row.open_amount) > 0 &&
    Number(row.open_amount) !== Number(row.amount);
  return (
    <div className="text-right font-mono">
      <div className="whitespace-nowrap font-medium">
        {fmtInvoiceMoney(row.amount, row.currency)}
      </div>
      {showOpen ? (
        <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">
          davon offen: {fmtInvoiceMoney(row.open_amount, row.currency, { marker: false })}
        </div>
      ) : null}
    </div>
  );
}

/** The one safe action per source, or nothing where the repo routes nothing. */
function ActionCell({ row }) {
  if (row.source === "native") {
    return (
      <Link
        to={`/admin/invoices/${row.id}`}
        aria-label={`${row.invoice_number || "Rechnung"} öffnen`}
        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
        Öffnen
      </Link>
    );
  }
  return (
    <span className="text-muted-foreground">
      —
      <span className="sr-only">
        Für Bexio-Rechnungen gibt es hier keine Einzelansicht.
      </span>
    </span>
  );
}

/** "There are none" — said in the terms of the world the reader is in. */
function EmptySource({ source }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Inbox aria-hidden="true" className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">Keine Rechnungen vorhanden</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        {source === "bexio"
          ? "Sobald eine Synchronisation gelaufen ist, erscheinen hier die Rechnungen aus Bexio."
          : "In Vantio erstellte Rechnungen erscheinen hier."}
      </p>
    </div>
  );
}

function SortIcon({ active, direction }) {
  if (!active) return <ChevronsUpDown aria-hidden="true" className="h-3 w-3 opacity-50" />;
  return direction === "asc" ? (
    <ArrowUp aria-hidden="true" className="h-3 w-3" />
  ) : (
    <ArrowDown aria-hidden="true" className="h-3 w-3" />
  );
}

export default function InvoiceTable({
  source,
  rows = [],
  total = 0,
  loading = false,
  error = "",
  filtered = false,
  onReset,
  onRetry,
  sort,
  direction,
  onSortChange,
  offset = 0,
  limit = 25,
  onOffsetChange,
  onLimitChange,
}) {
  const body = () => {
    if (error) return <BexioTableError message={error} onRetry={onRetry} />;
    if (loading) return <BexioTableSkeleton rows={8} columns={COLUMNS.length} />;
    if (rows.length === 0) {
      // A FILTERED empty state is source-neutral and is reused as it stands. An
      // UNFILTERED one is not: the shared component's copy says the rows appear
      // after a synchronisation, which is true of the mirror and false of Vantio
      // — nothing is synced into the native table, invoices are created in it.
      if (filtered) {
        return <BexioTableEmpty filtered noun="Rechnungen" onReset={onReset} />;
      }
      return <EmptySource source={source} />;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const active = col.sortField && col.sortField === sort;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      col.sortField
                        ? active
                          ? direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                        : undefined
                    }
                    className={`${TH} ${col.align === "text-right" ? "text-right" : ""} ${
                      col.narrow || ""
                    }`}
                  >
                    {col.sortField ? (
                      <button
                        type="button"
                        onClick={() => onSortChange?.(col.sortField)}
                        className={`inline-flex items-center gap-1 rounded uppercase tracking-[0.6px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          active ? "text-foreground" : ""
                        }`}
                      >
                        {col.label}
                        <SortIcon active={active} direction={direction} />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.source}-${row.id}`}
                className="border-b border-border last:border-b-0 hover:bg-muted/40"
              >
                <td className={`${TD} font-mono`}>
                  {row.invoice_number || <span className="text-muted-foreground">—</span>}
                </td>
                <td className={TD}>
                  <PartyCell row={row} />
                </td>
                <td className={`${TD} hidden text-muted-foreground lg:table-cell`}>
                  <PeriodCell period={row.period} periodStatus={row.period_status} />
                </td>
                <td className={`${TD} text-right`}>
                  <MoneyCell row={row} />
                </td>
                <td className={TD}>
                  <StatusPill status={row.canonical_status} />
                </td>
                <td className={`${TD} whitespace-nowrap font-mono text-muted-foreground`}>
                  {formatDateOnly(row.issue_date)}
                </td>
                <td
                  className={`${TD} whitespace-nowrap font-mono ${
                    row.canonical_status === "overdue"
                      ? "text-rose-700 dark:text-rose-300"
                      : "text-muted-foreground"
                  }`}
                >
                  {formatDateOnly(row.due_date)}
                </td>
                <td className={`${TD} text-right`}>
                  <ActionCell row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {body()}
      <BexioTablePagination
        total={total}
        offset={offset}
        limit={limit}
        onOffsetChange={onOffsetChange}
        onLimitChange={onLimitChange}
        noun="Rechnungen"
        busy={loading}
      />
    </div>
  );
}
